// frontend/src/features/campaign/components/create/Step3OfferMatrix.js
import React, { useMemo, useRef, useLayoutEffect, useEffect, useCallback } from 'react';

function AutoResizeTextarea({
  value,
  minHeight = 68,
  rows = 3,
  className = '',
  onChange,
  disabled,
  ...props
}) {
  const textareaRef = useRef(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [minHeight]);

  useLayoutEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  useEffect(() => {
    window.addEventListener('resize', adjustHeight);
    return () => window.removeEventListener('resize', adjustHeight);
  }, [adjustHeight]);

  return (
    <textarea
      ref={textareaRef}
      value={value ?? ''}
      rows={rows}
      disabled={disabled}
      className={`cmp-matrix-edit-input ${className}`.trim()}
      onChange={(e) => {
        e.target.style.height = 'auto';
        e.target.style.height = `${Math.max(e.target.scrollHeight, minHeight)}px`;
        if (onChange) onChange(e);
      }}
      {...props}
    />
  );
}

const STRATEGIC_ANGLES = [
  { id: 'roi', label: 'ROI & Revenue', icon: 'fas fa-chart-line' },
  { id: 'velocity', label: 'SDR Velocity', icon: 'fas fa-bolt' },
  { id: 'competitive', label: 'Competitive Edge', icon: 'fas fa-chess-knight' },
  { id: 'governance', label: 'Compliance & PDPA', icon: 'fas fa-shield-alt' },
];

export default function Step3OfferMatrix({
  formData,
  setFormData,
  isReadOnly = false,
  isGeneratingMatrix = false,
  generatingCardIndex = null,
  activeAngle = 'roi',
  hasGeneratedMatrix = false,
  onRegenerateMatrix,
  onRegenerateSingleCard,
  onUpdateMatrixRow,
  onRemoveMatrixRow,
  onAddMatrixRole,
}) {
  // Extract unique roles and attached leads from Step 2's imported leads
  const importedLeadRoles = useMemo(() => {
    if (!formData.importedLeads || formData.importedLeads.length === 0) return [];
    const map = new Map();
    formData.importedLeads.forEach((lead) => {
      const role = (lead.jobTitle || 'Executive Decision Maker').trim();
      if (!map.has(role)) {
        map.set(role, []);
      }
      map.get(role).push(lead);
    });
    return Array.from(map.entries()).map(([role, leads]) => ({
      role,
      leads,
      count: leads.length,
    }));
  }, [formData.importedLeads]);

  // Determine available roles pool: Step 2 imported lead roles if available, else Step 1 target personas
  const hasImportedLeads = formData.importedLeads && formData.importedLeads.length > 0;
  const availableRolesPool = useMemo(() => {
    if (hasImportedLeads && importedLeadRoles.length > 0) {
      return importedLeadRoles.map((r) => r.role);
    }
    return formData.targetPersonas && formData.targetPersonas.length > 0
      ? formData.targetPersonas
      : [];
  }, [hasImportedLeads, importedLeadRoles, formData.targetPersonas]);

  // Filter matrix strictly to in-scope roles from availableRolesPool
  const displayMatrix = useMemo(() => {
    if (!formData.matrix || formData.matrix.length === 0) return [];
    if (availableRolesPool.length === 0) return formData.matrix;
    const poolLower = new Set(availableRolesPool.map((r) => (r || '').trim().toLowerCase()));
    return formData.matrix.filter((m) => poolLower.has((m.persona || '').trim().toLowerCase()));
  }, [formData.matrix, availableRolesPool]);

  // Set of currently active personas in matrix
  const activePersonasSet = useMemo(() => {
    return new Set(displayMatrix.map((m) => (m.persona || '').trim().toLowerCase()));
  }, [displayMatrix]);

  // In-scope roles that can be added back
  const missingRoles = useMemo(() => {
    return availableRolesPool.filter(
      (roleName) => !activePersonasSet.has(roleName.trim().toLowerCase())
    );
  }, [availableRolesPool, activePersonasSet]);

  const getMatrixIndex = (persona, fallbackIdx) => {
    const idx = (formData.matrix || []).findIndex(
      (m) => (m.persona || '').trim().toLowerCase() === (persona || '').trim().toLowerCase()
    );
    return idx !== -1 ? idx : fallbackIdx;
  };

  const handleRemoveRole = (persona, fallbackIdx) => {
    if (isReadOnly || isGeneratingMatrix) return;
    const actualIdx = getMatrixIndex(persona, fallbackIdx);
    if (onRemoveMatrixRow) {
      onRemoveMatrixRow(actualIdx);
    } else {
      setFormData((prev) => ({
        ...prev,
        matrix: prev.matrix.filter((_, i) => i !== actualIdx),
      }));
    }
  };

  const handleAddRole = (roleName) => {
    if (isReadOnly || isGeneratingMatrix) return;
    // Strict validation: must be in availableRolesPool (from Tab 2 or Tab 1)
    const isAllowed = availableRolesPool.some((r) => r.toLowerCase() === roleName.toLowerCase());
    if (!isAllowed) return;

    if (onAddMatrixRole) {
      onAddMatrixRole(roleName);
    } else {
      const newCard = {
        persona: roleName,
        painPoint: '',
        valueProp: '',
        proofPoint: '',
        cta: '',
        objection: '',
      };

      setFormData((prev) => ({
        ...prev,
        matrix: [...prev.matrix, newCard],
      }));
    }
  };

  return (
    <div className={`cmp-blueprint-step-pane ${isReadOnly ? 'read-only' : ''}`}>
      <div className="cmp-blueprint-tag-pill">
        STEP 3 OF 6 • MESSAGING MATRIX & VALUE POSITIONING
      </div>

      <div className="cmp-step-intro-row">
        <div>
          <h2 className="cmp-blueprint-heading">Value Positioning & Persona Messaging Matrix</h2>
          <p className="cmp-blueprint-subheading">
            AI-generated strategic messaging mapping each decision-maker role to specific pain
            points, value propositions, proof points, and objection counters.
          </p>
        </div>
      </div>

      {/* ── Cohort Role Source Banner ── */}
      {importedLeadRoles.length > 0 ? (
        <div className="cmp-matrix-source-banner imported">
          <div className="cmp-matrix-source-left">
            <span className="cmp-source-icon-badge">
              <i className="fas fa-address-book" />
            </span>
            <div>
              <strong>
                Persona Frameworks Driven by Step 2 Imported Leads ({formData.importedLeads.length}{' '}
                leads)
              </strong>
              <p>
                Targeting {importedLeadRoles.length} unique executive role
                {importedLeadRoles.length > 1 ? 's' : ''}:{' '}
                {importedLeadRoles.map((r) => `${r.role} (${r.count})`).join(' • ')}
              </p>
            </div>
          </div>
          <div className="cmp-matrix-source-right">
            <span className="cmp-source-pill active">
              <i className="fas fa-check-circle" /> Step 2 Pipeline Cohort
            </span>
          </div>
        </div>
      ) : (
        <div className="cmp-matrix-source-banner fallback">
          <div className="cmp-matrix-source-left">
            <span className="cmp-source-icon-badge warning">
              <i className="fas fa-info-circle" />
            </span>
            <div>
              <strong>Using Step 1 Target Personas (No Leads Imported in Step 2)</strong>
              <p>
                Targeting general personas configured in Step 1. To tailor messaging to exact
                decision-maker titles, return to Step 2 and import contacts from your CRM Lead
                Pipeline.
              </p>
            </div>
          </div>
          <div className="cmp-matrix-source-right">
            <span className="cmp-source-pill warning">
              <i className="fas fa-sliders-h" /> Step 1 Personas
            </span>
          </div>
        </div>
      )}

      {/* ── Strategic Angle Selector & Regeneration Bar ── */}
      <div className="cmp-matrix-angles-bar">
        <div className="cmp-angles-bar-left">
          <span className="cmp-angles-label">
            <i className="fas fa-magic" /> AI Strategic Angle:
          </span>
          <div className="cmp-angles-chips">
            {STRATEGIC_ANGLES.map((ang) => (
              <button
                key={ang.id}
                type="button"
                className={`cmp-angle-chip-btn ${activeAngle === ang.id ? 'active' : ''}`}
                onClick={() => {
                  if (isReadOnly || isGeneratingMatrix) return;
                  if (onRegenerateMatrix) onRegenerateMatrix(ang.id);
                }}
                disabled={isGeneratingMatrix || isReadOnly}
                title={`Generate messaging emphasizing ${ang.label}`}
              >
                <i className={ang.icon} /> {ang.label}
              </button>
            ))}
          </div>
        </div>
        {!isReadOnly && onRegenerateMatrix && (
          <div className="cmp-angles-bar-right">
            <button
              type="button"
              className="cmp-btn-primary cmp-btn-matrix-regenerate"
              onClick={() => onRegenerateMatrix(activeAngle)}
              disabled={isGeneratingMatrix}
            >
              {isGeneratingMatrix ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Synthesizing with AI...
                </>
              ) : hasGeneratedMatrix ? (
                <>
                  <i className="fas fa-sync-alt" /> Regenerate Matrix with AI
                </>
              ) : (
                <>
                  <i className="fas fa-magic" /> Generate Matrix with AI
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Core Positioning Statement Card ── */}
      <div className="cmp-positioning-card">
        <div className="cmp-field-group">
          <label className="cmp-field-label">
            <span className="cmp-field-label-text">
              <i className="fas fa-compass" /> Core Positioning Statement:{' '}
              <span className="text-danger">*</span>
            </span>
          </label>
          <input
            type="text"
            className="cmp-field-input"
            value={formData.positioning}
            disabled={isReadOnly || isGeneratingMatrix}
            onChange={(e) => {
              if (isReadOnly || isGeneratingMatrix) return;
              setFormData({ ...formData, positioning: e.target.value });
            }}
            placeholder="Core value positioning statement..."
          />
        </div>
        <div className="cmp-field-group">
          <label className="cmp-field-label">
            <span className="cmp-field-label-text">
              <i className="fas fa-exclamation-circle" /> Primary Market Pain Points Addressed:{' '}
              <span className="text-danger">*</span>
            </span>
          </label>
          <input
            type="text"
            className="cmp-field-input"
            value={formData.painPoints}
            disabled={isReadOnly || isGeneratingMatrix}
            onChange={(e) => {
              if (isReadOnly || isGeneratingMatrix) return;
              setFormData({ ...formData, painPoints: e.target.value });
            }}
            placeholder="e.g. Low outbound reply rates, manual SDR prospect research..."
          />
        </div>

        {(formData.positioning?.trim() || formData.painPoints?.trim()) && (
          <div className="cmp-anchors-active-hint">
            <i className="fas fa-anchor" />
            <span>
              <strong>AI Anchors Active:</strong> AI generation will strictly derive persona pain
              points, value propositions, and proof points from your custom positioning and market
              challenges above.
            </span>
          </div>
        )}
      </div>

      {/* ── Matrix Cards List or Empty State ── */}
      {displayMatrix.length === 0 ? (
        <div
          className={`cmp-matrix-empty-state-box ${isGeneratingMatrix ? 'cmp-card-generating' : ''}`}
        >
          <div className="cmp-matrix-empty-icon-circle">
            <i className="fas fa-layer-group" />
          </div>
          <div>
            <h3 className="cmp-matrix-empty-title">No Persona Messaging Frameworks Active</h3>
            <p className="cmp-matrix-empty-subtitle">
              {hasImportedLeads
                ? `You have ${importedLeadRoles.length} unique decision-maker roles imported from Step 2. Synthesize AI messaging frameworks for all roles or activate them individually below.`
                : `Target personas are configured from Step 1. Generate full AI frameworks or activate individual in-scope roles below.`}
            </p>
          </div>

          {!isReadOnly && (
            <div className="cmp-matrix-empty-cta-wrap">
              {onRegenerateMatrix && (
                <button
                  type="button"
                  className="cmp-btn-primary cmp-matrix-empty-gen-btn"
                  onClick={() => onRegenerateMatrix(activeAngle)}
                  disabled={isGeneratingMatrix}
                >
                  {isGeneratingMatrix ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Synthesizing with AI...
                    </>
                  ) : hasGeneratedMatrix ? (
                    <>
                      <i className="fas fa-sync-alt" /> Regenerate All Frameworks with AI
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic" /> Generate All Frameworks with AI
                    </>
                  )}
                </button>
              )}

              {missingRoles.length > 0 && (
                <>
                  <div className="cmp-matrix-empty-divider">
                    <span>Or Add Individual In-Scope Roles</span>
                  </div>

                  <div className="cmp-matrix-empty-roles-grid">
                    {missingRoles.map((roleName) => {
                      const matchedImported = importedLeadRoles.find(
                        (r) => r.role.toLowerCase() === roleName.toLowerCase()
                      );
                      return (
                        <button
                          key={roleName}
                          type="button"
                          className="cmp-matrix-empty-role-btn"
                          disabled={isGeneratingMatrix || isReadOnly}
                          onClick={() => handleAddRole(roleName)}
                          title={`Add messaging framework for ${roleName}`}
                        >
                          <i className="fas fa-plus" />
                          <span>{roleName}</span>
                          {matchedImported && (
                            <span className="cmp-matrix-empty-lead-badge">
                              {matchedImported.count} lead{matchedImported.count > 1 ? 's' : ''}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={`cmp-matrix-list ${isGeneratingMatrix ? 'cmp-list-generating' : ''}`}>
          {displayMatrix.map((row, idx) => {
            const actualIdx = getMatrixIndex(row.persona, idx);
            // Check if this row's persona matches an imported lead title/role
            const matchedImported = importedLeadRoles.find(
              (r) => r.role.toLowerCase() === row.persona?.toLowerCase()
            );
            const isRowGenerating = generatingCardIndex === actualIdx;

            return (
              <div
                key={row.persona || idx}
                className={`cmp-matrix-card ${isGeneratingMatrix || isRowGenerating ? 'cmp-card-generating' : ''}`}
              >
                <div className="cmp-matrix-card-header">
                  <div className="cmp-matrix-header-left">
                    <span className="cmp-matrix-persona-tag">
                      <i className="fas fa-user-tie" /> {row.persona}
                    </span>
                    {matchedImported && (
                      <span
                        className="cmp-matrix-lead-count"
                        title={`Attached leads: ${matchedImported.leads
                          .map((l) => `${l.name} (${l.company})`)
                          .join(', ')}`}
                      >
                        <i className="fas fa-users" /> {matchedImported.count} Lead
                        {matchedImported.count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="cmp-matrix-header-right">
                    <span className="cmp-matrix-step-badge">Persona Framework #{idx + 1}</span>
                    {!isReadOnly && onRegenerateSingleCard && (
                      <button
                        type="button"
                        className="cmp-btn-single-regenerate"
                        disabled={isGeneratingMatrix || isRowGenerating}
                        onClick={() => onRegenerateSingleCard(actualIdx, activeAngle)}
                        title="Regenerate this persona framework with an alternative AI angle"
                      >
                        {isRowGenerating ? (
                          <>
                            <i className="fas fa-spinner fa-spin" /> Regenerating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sparkles" /> AI Variations
                          </>
                        )}
                      </button>
                    )}
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="cmp-btn-remove-matrix"
                        disabled={isGeneratingMatrix || isRowGenerating}
                        onClick={() => handleRemoveRole(row.persona, actualIdx)}
                        title={`Remove ${row.persona} from Messaging Matrix`}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="cmp-matrix-grid">
                  <div className="cmp-matrix-cell">
                    <label className="cmp-matrix-label">
                      <i className="fas fa-exclamation-triangle text-warning" /> Primary Pain Point{' '}
                      <span className="text-danger">*</span>
                    </label>
                    <AutoResizeTextarea
                      rows={3}
                      minHeight={68}
                      value={row.painPoint}
                      disabled={isReadOnly || isGeneratingMatrix || isRowGenerating}
                      onChange={(e) => {
                        if (isReadOnly || isGeneratingMatrix || isRowGenerating) return;
                        onUpdateMatrixRow(actualIdx, 'painPoint', e.target.value);
                      }}
                      placeholder="Describe the persona's key pain point..."
                    />
                  </div>
                  <div className="cmp-matrix-cell">
                    <label className="cmp-matrix-label">
                      <i className="fas fa-gem text-primary" /> Value Proposition{' '}
                      <span className="text-danger">*</span>
                    </label>
                    <AutoResizeTextarea
                      rows={3}
                      minHeight={68}
                      value={row.valueProp}
                      disabled={isReadOnly || isGeneratingMatrix || isRowGenerating}
                      onChange={(e) => {
                        if (isReadOnly || isGeneratingMatrix || isRowGenerating) return;
                        onUpdateMatrixRow(actualIdx, 'valueProp', e.target.value);
                      }}
                      placeholder="Tailored value proposition..."
                    />
                  </div>
                  <div className="cmp-matrix-cell">
                    <label className="cmp-matrix-label">
                      <i className="fas fa-award text-success" /> Proof Point / Case Study{' '}
                      <span className="text-danger">*</span>
                    </label>
                    <AutoResizeTextarea
                      rows={3}
                      minHeight={68}
                      value={row.proofPoint}
                      disabled={isReadOnly || isGeneratingMatrix || isRowGenerating}
                      onChange={(e) => {
                        if (isReadOnly || isGeneratingMatrix || isRowGenerating) return;
                        onUpdateMatrixRow(actualIdx, 'proofPoint', e.target.value);
                      }}
                      placeholder="Metric or client case study proof point..."
                    />
                  </div>
                  <div className="cmp-matrix-cell">
                    <label className="cmp-matrix-label">
                      <i className="fas fa-mouse-pointer text-info" /> Call-To-Action (CTA){' '}
                      <span className="text-danger">*</span>
                    </label>
                    <AutoResizeTextarea
                      rows={3}
                      minHeight={68}
                      value={row.cta}
                      disabled={isReadOnly || isGeneratingMatrix || isRowGenerating}
                      onChange={(e) => {
                        if (isReadOnly || isGeneratingMatrix || isRowGenerating) return;
                        onUpdateMatrixRow(actualIdx, 'cta', e.target.value);
                      }}
                      placeholder="Next-step call to action..."
                    />
                  </div>
                </div>

                <div className="cmp-objection-box">
                  <label className="cmp-matrix-label">
                    <i className="fas fa-shield-virus" /> Objection Handling Strategy:{' '}
                    <span className="text-danger">*</span>
                  </label>
                  <AutoResizeTextarea
                    className="cmp-objection-input"
                    rows={2}
                    minHeight={52}
                    value={row.objection}
                    disabled={isReadOnly || isGeneratingMatrix || isRowGenerating}
                    onChange={(e) => {
                      if (isReadOnly || isGeneratingMatrix || isRowGenerating) return;
                      onUpdateMatrixRow(actualIdx, 'objection', e.target.value);
                    }}
                    placeholder="How to overcome common objections..."
                  />
                </div>
              </div>
            );
          })}

          {!isReadOnly && missingRoles.length > 0 && (
            <div className="cmp-matrix-missing-roles-footer">
              <div className="cmp-matrix-empty-divider">
                <span>Or Add Individual In-Scope Roles</span>
              </div>
              <div className="cmp-matrix-empty-roles-grid">
                {missingRoles.map((roleName) => {
                  const matchedImported = importedLeadRoles.find(
                    (r) => r.role.toLowerCase() === roleName.toLowerCase()
                  );
                  return (
                    <button
                      key={roleName}
                      type="button"
                      className="cmp-matrix-empty-role-btn"
                      disabled={isGeneratingMatrix || isReadOnly}
                      onClick={() => handleAddRole(roleName)}
                      title={`Add messaging framework for ${roleName}`}
                    >
                      <i className="fas fa-plus" />
                      <span>{roleName}</span>
                      {matchedImported && (
                        <span className="cmp-matrix-empty-lead-badge">
                          {matchedImported.count} lead{matchedImported.count > 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
